import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface TimezoneInfo {
  detected_timezone: string;
  timezone_offset: string;
  confidence_score: number;
  sample_dates: string[];
}

interface UseTimezoneInfoResult {
  timezoneInfo: Record<string, TimezoneInfo>;
  loading: boolean;
  error: string | null;
  getTimezoneForConnection: (connectionId: string, tableName?: string) => TimezoneInfo | null;
  getAllTimezones: () => TimezoneInfo[];
  hasMultipleTimezones: () => boolean;
}

export const useTimezoneInfo = (connectionIds: string[]): UseTimezoneInfoResult => {
  const [timezoneInfo, setTimezoneInfo] = useState<Record<string, TimezoneInfo>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (connectionIds.length === 0) return;

    const fetchTimezoneInfo = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch timezone info for all connections
        const { data: tzData, error: tzError } = await supabase
          .from('csv_import_timezone_info')
          .select('*')
          .in('connection_id', connectionIds);

        if (tzError) {
          console.error('Error fetching timezone info:', tzError);
          setError('Failed to fetch timezone information');
          return;
        }

        // Fetch timezone info from job records as fallback
        const { data: jobData, error: jobError } = await (supabase
          .from('csv_import_jobs') as any)
          .select('connection_id, detected_timezone, timezone_offset, timezone_confidence')
          .in('connection_id', connectionIds)
          .not('detected_timezone', 'is', null);

        if (jobError) {
          console.warn('Error fetching job timezone info:', jobError);
        }

        // Combine both sources
        const combined: Record<string, TimezoneInfo> = {};

        // Process timezone info table data (primary source)
        tzData?.forEach(item => {
          const key = `${item.connection_id}_${item.table_name}`;
          combined[key] = {
            detected_timezone: item.detected_timezone,
            timezone_offset: item.timezone_offset,
            confidence_score: item.confidence_score || 1.0,
            sample_dates: item.sample_dates || []
          };
        });

        // Process job data as fallback
        jobData?.forEach(item => {
          const key = item.connection_id;
          if (!combined[key] && item.detected_timezone) {
            combined[key] = {
              detected_timezone: item.detected_timezone,
              timezone_offset: item.timezone_offset || '+00:00',
              confidence_score: item.timezone_confidence || 1.0,
              sample_dates: []
            };
          }
        });

        setTimezoneInfo(combined);
      } catch (err) {
        console.error('Error in fetchTimezoneInfo:', err);
        setError('Failed to fetch timezone information');
      } finally {
        setLoading(false);
      }
    };

    fetchTimezoneInfo();
  }, [connectionIds]);

  const getTimezoneForConnection = (connectionId: string, tableName?: string): TimezoneInfo | null => {
    // Try with table name first
    if (tableName) {
      const withTable = timezoneInfo[`${connectionId}_${tableName}`];
      if (withTable) return withTable;
    }
    
    // Fallback to connection ID only
    return timezoneInfo[connectionId] || null;
  };

  const getAllTimezones = (): TimezoneInfo[] => {
    const allTimezones = Object.values(timezoneInfo);
    // Remove duplicates based on timezone and offset combination
    return allTimezones.reduce((unique, tz) => {
      const key = `${tz.detected_timezone}_${tz.timezone_offset}`;
      if (!unique.find(u => `${u.detected_timezone}_${u.timezone_offset}` === key)) {
        unique.push(tz);
      }
      return unique;
    }, [] as TimezoneInfo[]);
  };

  const hasMultipleTimezones = (): boolean => {
    return getAllTimezones().length > 1;
  };

  return {
    timezoneInfo,
    loading,
    error,
    getTimezoneForConnection,
    getAllTimezones,
    hasMultipleTimezones
  };
};